import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * MSG91WidgetModal
 *
 * Wraps the MSG91 OTP widget inside our Dialog shell.
 * The widget renders its own phone + OTP fields; this component is
 * just the container, loading state, error display, and the name-capture
 * step that runs after the widget successfully verifies the user.
 *
 * Flow:
 *   1. Dialog opens → script already injected by useMSG91Script → call initSendOTP.
 *   2. User completes OTP in widget → success callback fires with JWT.
 *   3. POST /vendor/auth/verify-widget-token with JWT → server sets ss_vendor cookie.
 *   4. If vendor.name is null → show NameStep (skippable).
 *   5. Call VendorModalCallbacks._onLoginSuccess → requireAuth() resolves.
 *
 * On failure the widget callback surfaces a friendly message and the modal
 * stays open so the user can retry.
 */
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { api, AppError } from "@/lib/api";
import { VendorModalCallbacks } from "./vendorModalCallbacks";
// ── FieldError (shared) ───────────────────────────────────────────────────────
function FieldError({ msg }) {
    if (!msg)
        return null;
    return _jsx("p", { className: "mt-1 text-xs text-status-danger", children: msg });
}
function NameStep({ onSave, onSkip: _onSkip }) {
    const [name, setName] = useState("");
    const [businessName, setBusinessName] = useState("");
    const [location, setLocation] = useState("");
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const trimmedName = name.trim();
    const trimmedBusiness = businessName.trim();
    const trimmedLocation = location.trim();
    const trimmedEmail = email.trim();
    const emailValid = trimmedEmail === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
    const canSubmit = trimmedName.length >= 2 &&
        trimmedBusiness.length >= 2 &&
        emailValid &&
        !submitting;
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (trimmedName.length < 2) {
            setError("Please enter your name.");
            return;
        }
        if (trimmedBusiness.length < 2) {
            setError("Please enter your business name.");
            return;
        }
        if (!emailValid) {
            setError("That email doesn't look right.");
            return;
        }
        setSubmitting(true);
        try {
            await onSave({
                name: trimmedName,
                businessName: trimmedBusiness,
                ...(trimmedLocation ? { location: trimmedLocation } : {}),
                ...(trimmedEmail ? { email: trimmedEmail } : {}),
            });
        }
        catch {
            setError("Could not save. Please try again.");
        }
        finally {
            setSubmitting(false);
        }
    }, [trimmedName, trimmedBusiness, trimmedLocation, trimmedEmail, emailValid, onSave]);
    const inputCls = "w-full rounded-input border border-border-subtle px-3 py-2.5 text-sm text-text " +
        "placeholder:text-text-tertiary bg-surface-2 outline-none transition-all " +
        "focus:border-[#9868cc] focus:ring-2 focus:ring-[#9868cc]/20";
    return (_jsxs("form", { onSubmit: (e) => void handleSubmit(e), className: "space-y-4", noValidate: true, children: [_jsxs("div", { className: "text-center space-y-1", children: [_jsx("div", { className: "w-14 h-14 rounded-full bg-[#9868cc]/20 border border-[#9868cc]/30 flex items-center justify-center mx-auto mb-2", children: _jsx("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "#9868cc", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className: "w-7 h-7", "aria-hidden": "true", children: _jsx("polyline", { points: "20 6 9 17 4 12" }) }) }), _jsx("h2", { className: "font-display italic text-2xl text-text", children: "You're verified!" }), _jsx("p", { className: "text-sm text-text-muted", children: "Quick profile so organizers know who's reaching out." })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "msg91-vendor-name", className: "block text-sm font-medium text-text mb-1", children: "Your name" }), _jsx("input", { id: "msg91-vendor-name", type: "text", autoComplete: "name", autoFocus: true, placeholder: "e.g. Priya Reddy", value: name, onChange: (e) => {
                            setName(e.target.value);
                            if (error)
                                setError(null);
                        }, className: inputCls })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "msg91-vendor-business", className: "block text-sm font-medium text-text mb-1", children: "Business name" }), _jsx("input", { id: "msg91-vendor-business", type: "text", autoComplete: "organization", placeholder: "e.g. Priya Jewellery", value: businessName, onChange: (e) => {
                            setBusinessName(e.target.value);
                            if (error)
                                setError(null);
                        }, className: inputCls })] }), _jsxs("div", { children: [_jsxs("label", { htmlFor: "msg91-vendor-location", className: "block text-sm font-medium text-text mb-1", children: ["Where are you based", " ", _jsx("span", { className: "text-text-tertiary font-normal", children: "(optional)" })] }), _jsx("input", { id: "msg91-vendor-location", type: "text", autoComplete: "address-level2", placeholder: "e.g. Banjara Hills, Hyderabad", value: location, onChange: (e) => {
                            setLocation(e.target.value);
                            if (error)
                                setError(null);
                        }, className: inputCls })] }), _jsxs("div", { children: [_jsxs("label", { htmlFor: "msg91-vendor-email", className: "block text-sm font-medium text-text mb-1", children: ["Email", " ", _jsx("span", { className: "text-text-tertiary font-normal", children: "(optional)" })] }), _jsx("input", { id: "msg91-vendor-email", type: "email", autoComplete: "email", placeholder: "you@example.com", value: email, onChange: (e) => {
                            setEmail(e.target.value);
                            if (error)
                                setError(null);
                        }, className: inputCls }), error && _jsx(FieldError, { msg: error })] }), _jsx("button", { type: "submit", disabled: !canSubmit, className: "cta-pill w-full disabled:opacity-60 disabled:cursor-not-allowed", children: submitting ? "Saving..." : "Continue" })] }));
}
function WidgetStep({ onVerified, onError, scriptReady }) {
    const widgetId = import.meta.env.VITE_MSG91_WIDGET_ID;
    const tokenAuth = import.meta.env.VITE_MSG91_TOKEN_AUTH;
    const initiated = useRef(false);
    useEffect(() => {
        if (!scriptReady)
            return;
        if (initiated.current)
            return;
        if (!window.initSendOTP) {
            onError("OTP widget not available. Please refresh and try again.");
            return;
        }
        initiated.current = true;
        window.initSendOTP({
            widgetId,
            tokenAuth,
            exposeMethods: false,
            success: (data) => {
                const accessToken = data.message;
                // Exchange the MSG91 JWT for our session cookie.
                api
                    .post("/vendor/auth/verify-widget-token", { accessToken })
                    .then((res) => {
                    onVerified(res.data.vendor);
                })
                    .catch((err) => {
                    const msg = err instanceof AppError
                        ? err.message
                        : "Verification failed. Please try again.";
                    onError(msg);
                });
            },
            failure: (error) => {
                // MSG91 calls failure on cancellation too — keep the message generic.
                console.warn("[MSG91Widget] failure callback", error);
                onError("OTP verification failed. Please try again.");
            },
        });
    }, [scriptReady, widgetId, tokenAuth, onVerified, onError]);
    if (!scriptReady) {
        return (_jsxs("div", { className: "flex flex-col items-center gap-4 py-8", children: [_jsx("div", { className: "w-8 h-8 border-2 border-[#9868cc]/30 border-t-[#9868cc] rounded-full animate-spin", role: "status", "aria-label": "Loading OTP widget" }), _jsx("p", { className: "text-sm text-text-muted", children: "Loading secure verification..." })] }));
    }
    // The widget renders its own UI into the DOM; no explicit JSX needed.
    // Show a minimal wrapper so the modal isn't blank during widget init.
    return (_jsxs("div", { className: "space-y-3", children: [_jsxs("div", { className: "text-center space-y-1 mb-4", children: [_jsx("h2", { className: "font-display italic text-2xl text-text", children: "Verify your number" }), _jsx("p", { className: "text-sm text-text-muted", children: "We'll send a one-time code so we know it's you." })] }), _jsx("p", { className: "text-xs text-text-tertiary text-center", children: "Your number identifies you across events. No spam, ever." })] }));
}
export function MSG91WidgetModal({ open, onOpenChange, scriptReady }) {
    const callbacks = useContext(VendorModalCallbacks);
    const [step, setStep] = useState("widget");
    const [pendingVendor, setPendingVendor] = useState(null);
    const [error, setError] = useState(null);
    // Reset on close.
    useEffect(() => {
        if (!open) {
            const t = setTimeout(() => {
                setStep("widget");
                setPendingVendor(null);
                setError(null);
            }, 300);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [open]);
    const handleVerified = useCallback((vendor) => {
        setError(null);
        if (!vendor.name || !vendor.businessName) {
            setPendingVendor(vendor);
            setStep("name");
        }
        else {
            callbacks?._onLoginSuccess(vendor);
        }
    }, [callbacks]);
    const handleWidgetError = useCallback((msg) => {
        setError(msg);
    }, []);
    async function handleNameSave(fields) {
        if (!pendingVendor)
            return;
        try {
            const res = await api.patch("/vendor/me", fields);
            callbacks?._onLoginSuccess(res.data.vendor);
        }
        catch {
            callbacks?._onLoginSuccess(pendingVendor);
        }
    }
    function handleNameSkip() {
        if (pendingVendor) {
            callbacks?._onLoginSuccess(pendingVendor);
        }
    }
    // During the widget step, run the dialog NON-modal so Radix doesn't trap
    // focus or make the MSG91 widget inert (that froze typing + the submit
    // button). The escape handlers keep our dialog from stealing focus or
    // dismissing while the user works inside the widget.
    const widgetProps = step === "widget"
        ? {
            modal: false,
            onOpenAutoFocus: (e) => e.preventDefault(),
            onInteractOutside: (e) => e.preventDefault(),
            onFocusOutside: (e) => e.preventDefault(),
        }
        : step === "name"
            ? {
                // Cookie is already set at this point — locking close so the
                // user can't bypass the name field and walk away nameless.
                onInteractOutside: (e) => e.preventDefault(),
            }
            : {};
    return (_jsx(Dialog, { open: open, onOpenChange: (next) => {
            if (!next && step === "name")
                return;
            onOpenChange(next);
        }, ...widgetProps, children: _jsxs("div", { className: "pt-2", children: [error && (_jsxs("div", { role: "alert", className: "mb-4 flex items-start gap-2 bg-status-danger/10 border border-status-danger/30\n              rounded-input px-3 py-2.5 text-sm text-status-danger", children: [_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className: "w-4 h-4 shrink-0 mt-0.5", "aria-hidden": "true", children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("line", { x1: "12", y1: "8", x2: "12", y2: "12" }), _jsx("line", { x1: "12", y1: "16", x2: "12.01", y2: "16" })] }), error] })), step === "widget" && (_jsx(WidgetStep, { scriptReady: scriptReady, onVerified: handleVerified, onError: handleWidgetError })), step === "name" && (_jsx(NameStep, { onSave: handleNameSave, onSkip: handleNameSkip }))] }) }));
}
